import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination,
  Chip, IconButton, TextField, Select, MenuItem, FormControl,
  InputLabel, Button, GridLegacy as Grid, Card, CardContent, Tooltip, Avatar,
  LinearProgress, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, InputAdornment, Collapse
} from '@mui/material';
import {
  Assessment, Person, Business, AccessTime, Search,
  FilterList, Refresh, GetApp, Email, Phone, Score,
  Clear, TrendingUp, Assignment, CalendarToday,
  ExpandLess, ExpandMore
} from '@mui/icons-material';
import { API_PREFIX } from '../../services/api';
import ResumeActions from '../shared/ResumeViewer';

const OptimizedCandidatePipeline = ({ vendorId }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    per_page: 25,
    total: 0,
    pages: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    stage: '',
    status: '',
    job_id: '',
    date_from: '',
    date_to: ''
  });

  // UI States
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // All your existing functions remain the same...
  const loadCandidates = async (page = 0, newFilters = null) => {
    setLoading(true);
    try {
      const filtersToUse = newFilters || filters;
      const params = new URLSearchParams({
        page: page + 1,
        per_page: pagination.per_page,
        ...Object.fromEntries(
          Object.entries(filtersToUse).filter(([_, value]) => value !== '')
        )
      });

      const response = await fetch(`${API_PREFIX}/vendor/candidates-pipeline?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCandidates(data.candidates);
        setPagination(prev => ({
          ...prev,
          page: data.pagination.page - 1,
          total: data.pagination.total,
          pages: data.pagination.pages
        }));
        setStats(data.statistics);
      }
    } catch (error) {
      console.error('Failed to load candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await fetch(`${API_PREFIX}/vendor/dashboard`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.assigned_jobs || []);
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
    }
  };

  useEffect(() => {
    loadJobs();
    loadCandidates();
  }, []);

  const handlePageChange = (event, newPage) => {
    loadCandidates(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPagination(prev => ({ ...prev, per_page: newPerPage, page: 0 }));
    loadCandidates(0);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0 }));
    loadCandidates(0, newFilters);
  };

  const handleViewScores = (candidate) => {
    console.log('Selected candidate data:', candidate);
    
    setSelectedCandidate({
      ...candidate,
      scores: {
        cvScore: candidate.cv_score || 'Not scored',
        cvStatus: candidate.cv_feedback_status || 'Not reviewed',
        cvNotes: candidate.cv_feedback_notes || 'No feedback available',
        cvStrengths: candidate.cv_strengths || 'No strengths noted',
        cvWeaknesses: candidate.cv_weaknesses || 'No weaknesses noted',
        cvExperienceRelevance: candidate.cv_experience_relevance || 'Not rated',
        cvEducationFit: candidate.cv_education_fit || 'Not rated',
        cvDomainKnowledge: candidate.cv_domain_knowledge || 'Not rated',
        cvOverallImpression: candidate.cv_overall_impression || 'Not rated',
        cvFeedbackDate: candidate.cv_feedback_date,
        
        interviewScore: candidate.interview_score || 'Not interviewed',
        interviewStatus: candidate.interview_status || 'Not interviewed',
        interviewVerdict: candidate.interview_verdict || 'No verdict',
        interviewFeedback: candidate.interview_feedback || 'No feedback available',
        interviewTechnicalRating: candidate.interview_technical_rating || 'Not rated',
        interviewCommunicationRating: candidate.interview_communication_rating || 'Not rated',
        interviewProblemSolvingRating: candidate.interview_problem_solving_rating || 'Not rated',
        interviewCulturalFitRating: candidate.interview_cultural_fit_rating || 'Not rated',
        interviewScheduledAt: candidate.interview_scheduled_at,
        interviewCompletedAt: candidate.interview_completed_at
      }
    });
    setScoreDialogOpen(true);
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '', stage: '', status: '', job_id: '', date_from: '', date_to: ''
    };
    setFilters(clearedFilters);
    loadCandidates(0, clearedFilters);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'hired': 'success',
      'rejected': 'error',
      'interview_scheduled': 'info',
      'cv_reviewed': 'primary'
    };
    return colors[status] || 'default';
  };

  const getStageColor = (stage) => {
    const colors = {
      'applied': 'default',
      'cv_submitted': 'info',
      'cv_reviewed': 'primary',
      'interview_scheduled': 'warning',
      'interviewed': 'secondary',
      'hired': 'success',
      'rejected': 'error'
    };
    return colors[stage] || 'default';
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      {/* Search & Filters Section */}
      <Paper sx={{ overflow: 'hidden', mt: -1 }}>
        <Box 
          sx={{ 
            height: 36,
            px: 1.5,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            bgcolor: hasActiveFilters ? 'primary.50' : 'transparent',
            borderBottom: filtersExpanded ? '1px solid' : 'none',
            borderColor: 'divider'
          }}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FilterList sx={{ fontSize: '1.1rem' }} color={hasActiveFilters ? 'primary' : 'inherit'} />
            <Typography variant="body1" fontWeight="500" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>Search & Filters</span>
              {hasActiveFilters && (
                <Chip 
                  label={`${Object.values(filters).filter(v => v !== '').length} active`} 
                  size="small" 
                  color="primary"
                  sx={{ height: '20px' }} 
                />
              )}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilters();
                }}
              >
                Clear All
              </Button>
            )}
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={(e) => {
                e.stopPropagation();
                loadCandidates(0);
              }}
            >
              Refresh
            </Button>
            {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
        </Box>
        
        <Collapse in={filtersExpanded}>
          <Box sx={{ px: 1.5, py: 0.5 }}>
            <Grid container spacing={1} alignItems="center">
              {/* Quick Search with minimal height */}
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name, email, phone..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 32,
                      backgroundColor: 'white',
                      '& input': {
                        p: 0,
                        fontSize: '0.8125rem',
                        '&::placeholder': {
                          color: 'text.secondary',
                          opacity: 0.8
                        },
                        color: 'primary.main',
                        fontWeight: 500
                      }
                    }
                  }}
                />
              </Grid>
              
              {/* ✅ ENHANCED Job Filter with clear labels */}
              <Grid item xs={12} md={4}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    mb: 0.5, 
                    fontWeight: 600, 
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem'
                  }}
                >
                  💼 Filter by Job Position
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: 'primary.main', fontWeight: 500 }}>
                    Select Job Position
                  </InputLabel>
                  <Select
                    value={filters.job_id}
                    onChange={(e) => handleFilterChange('job_id', e.target.value)}
                    label="Select Job Position"
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em style={{ color: '#666' }}>All Job Positions</em>
                    </MenuItem>
                    {jobs.map((job) => (
                      <MenuItem key={job.id} value={job.id}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {job.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {job.location} • ID: {job.id}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Stage Filter */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <Select
                    value={filters.stage}
                    onChange={(e) => handleFilterChange('stage', e.target.value)}
                    displayEmpty
                    sx={{
                      height: 32,
                      backgroundColor: 'white',
                      '& .MuiSelect-select': {
                        py: 0,
                        fontSize: '0.8125rem'
                      }
                    }}
                    renderValue={(value) => value ? <Chip label={value.replace('_', ' ').toUpperCase()} size="small" color="primary" /> : 'Filter by Stage'}
                  >
                    <MenuItem value="">
                      <em style={{ color: '#666' }}>All Stages</em>
                    </MenuItem>
                    <MenuItem value="cv_submitted">
                      <Chip label="CV Submitted" color="info" size="small" />
                    </MenuItem>
                    <MenuItem value="cv_reviewed">
                      <Chip label="CV Reviewed" color="primary" size="small" />
                    </MenuItem>
                    <MenuItem value="interview_scheduled">
                      <Chip label="Interview Scheduled" color="warning" size="small" />
                    </MenuItem>
                    <MenuItem value="interviewed">
                      <Chip label="Interviewed" color="secondary" size="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Status Filter */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    displayEmpty
                    sx={{
                      height: 32,
                      backgroundColor: 'white',
                      '& .MuiSelect-select': {
                        py: 0,
                        fontSize: '0.8125rem'
                      }
                    }}
                    renderValue={(value) => value ? <Chip label={value.toUpperCase()} size="small" color={getStatusColor(value)} /> : 'Filter by Status'}
                  >
                    <MenuItem value="">
                      <em style={{ color: '#666' }}>All Statuses</em>
                    </MenuItem>
                    <MenuItem value="pending">
                      <Chip label="Pending" color="warning" size="small" />
                    </MenuItem>
                    <MenuItem value="hired">
                      <Chip label="Hired" color="success" size="small" />
                    </MenuItem>
                    <MenuItem value="rejected">
                      <Chip label="Rejected" color="error" size="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Candidates Table */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              My Candidates Pipeline <span style={{ color: '#666', fontSize: '0.9rem' }}>({pagination.total})</span>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Page {pagination.page + 1} of {pagination.pages}
            </Typography>
          </Box>
          {loading && <LinearProgress sx={{ mt: 1 }} />}
        </Box>

        {/* Table Container - Scrollable */}
        <TableContainer sx={{ flex: 1, '& .MuiTableCell-root': { px: 1.5, py: 0.75 } }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f5f5f5' } }}>
                <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Candidate</TableCell>
                <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>Stage</TableCell>
                <TableCell sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>CV Score</TableCell>
                <TableCell sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>Interview</TableCell>
                <TableCell sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map((candidate) => (
                <TableRow key={candidate.id} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                  {/* Candidate Info */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main', 
                          width: 32, 
                          height: 32,
                          fontSize: '0.8rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {candidate.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="500" sx={{ lineHeight: 1.1, mb: 0.25 }}>
                          {candidate.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          {candidate.email}
                        </Typography>
                        {candidate.phone && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                            <Phone sx={{ fontSize: 10 }} /> {candidate.phone}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Job Details */}
                  <TableCell>
                    <Box sx={{ minWidth: 140 }}>
                      <Typography variant="body2" fontWeight="500" sx={{ lineHeight: 1, mb: 0.25 }}>
                        {candidate.job_title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        <span>ID: {candidate.job_id}</span>
                        {candidate.experience_years && (
                          <span style={{ marginLeft: '8px' }}>{candidate.experience_years}y exp</span>
                        )}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Stage */}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip
                      label={candidate.current_stage?.replace('_', ' ').toUpperCase()}
                      color={getStageColor(candidate.current_stage)}
                      size="small"
                      sx={{ fontSize: '0.7rem', minWidth: 100 }}
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip
                      label={candidate.final_status?.toUpperCase()}
                      color={getStatusColor(candidate.final_status)}
                      size="small"
                      sx={{ fontSize: '0.7rem', minWidth: 80 }}
                    />
                  </TableCell>

                  {/* CV Score */}
                  <TableCell sx={{ textAlign: 'center' }}>
                    {candidate.cv_score ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Score sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2" fontWeight="600">
                          {parseFloat(candidate.cv_score).toFixed(1)}/10
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not scored
                      </Typography>
                    )}
                  </TableCell>

                  {/* Interview Score */}
                  <TableCell sx={{ textAlign: 'center' }}>
                    {candidate.interview_score ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Score sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2" fontWeight="600">
                          {parseFloat(candidate.interview_score).toFixed(1)}/10
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not interviewed
                      </Typography>
                    )}
                  </TableCell>

                  {/* Submitted Date */}
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', textAlign: 'center' }}>
                      {new Date(candidate.submitted_at).toLocaleDateString()}<br />
                      {new Date(candidate.submitted_at).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Tooltip title="View Scores & Feedback">
                      <IconButton
                        size="small"
                        onClick={() => handleViewScores(candidate)}
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { bgcolor: 'primary.50' }
                        }}
                      >
                        <Assessment />
                      </IconButton>
                    </Tooltip>
                    {/* Resume preview / download for the recruiter's own submissions */}
                    <ResumeActions candidateId={candidate.id} candidateName={candidate.name} variant="icons" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination - Fixed at bottom */}
        <TablePagination
          rowsPerPageOptions={[15, 25, 50, 100]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.per_page}
          page={pagination.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          sx={{ 
            borderTop: '1px solid', 
            borderColor: 'divider',
            '& .MuiToolbar-root': {
              minHeight: '48px',
              p: 0.5
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.875rem'
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.75rem'
            }
          }}
        />
      </Paper>

      {/* Score Dialog - Keep unchanged */}
      <Dialog
        open={scoreDialogOpen}
        onClose={() => setScoreDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 8,
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span">
            Candidate Scores & Feedback: {selectedCandidate?.name}
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* CV Review Section */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    CV Review
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Score</Typography>
                    <Typography variant="h5" color="primary">
                      {selectedCandidate?.scores?.cvScore || 'Not scored'}{typeof selectedCandidate?.scores?.cvScore === 'number' ? '/10' : ''}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip 
                      label={selectedCandidate?.scores?.cvStatus || 'Not reviewed'} 
                      size="small"
                      color={selectedCandidate?.scores?.cvStatus === 'Not reviewed' ? 'default' : 'primary'}
                    />
                  </Box>
                  
                  {selectedCandidate?.scores?.cvFeedbackDate && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Reviewed Date</Typography>
                      <Typography variant="body2">
                        {new Date(selectedCandidate.scores.cvFeedbackDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Feedback Notes
                    </Typography>
                    <Typography variant="body2">
                      {selectedCandidate?.scores?.cvNotes || 'No feedback available'}
                    </Typography>
                  </Box>
                  
                  {selectedCandidate?.scores?.cvStrengths !== 'No strengths noted' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="success.main" gutterBottom>
                        Strengths
                      </Typography>
                      <Typography variant="body2">
                        {selectedCandidate?.scores?.cvStrengths || 'No strengths noted'}
                      </Typography>
                    </Box>
                  )}
                  
                  {selectedCandidate?.scores?.cvWeaknesses !== 'No weaknesses noted' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="warning.main" gutterBottom>
                        Areas for Improvement
                      </Typography>
                      <Typography variant="body2">
                        {selectedCandidate?.scores?.cvWeaknesses || 'No weaknesses noted'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Interview Section */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Interview
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Score</Typography>
                    <Typography variant="h5" color="success.main">
                      {selectedCandidate?.scores?.interviewScore || 'Not interviewed'}{typeof selectedCandidate?.scores?.interviewScore === 'number' ? '/10' : ''}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip 
                      label={selectedCandidate?.scores?.interviewStatus || 'Not interviewed'} 
                      size="small"
                      color={selectedCandidate?.scores?.interviewStatus === 'Completed' ? 'success' : 'default'}
                    />
                  </Box>
                  
                  {selectedCandidate?.scores?.interviewVerdict !== 'No verdict' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Verdict</Typography>
                      <Chip 
                        label={selectedCandidate?.scores?.interviewVerdict || 'No verdict'} 
                        size="small"
                        color={
                          selectedCandidate?.scores?.interviewVerdict === 'hire' ? 'success' :
                          selectedCandidate?.scores?.interviewVerdict === 'reject' ? 'error' : 'default'
                        }
                      />
                    </Box>
                  )}
                  
                  {(selectedCandidate?.scores?.interviewScheduledAt || selectedCandidate?.scores?.interviewCompletedAt) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCandidate?.scores?.interviewCompletedAt ? 'Completed' : 'Scheduled'} Date
                      </Typography>
                      <Typography variant="body2">
                        {selectedCandidate?.scores?.interviewCompletedAt ? 
                          new Date(selectedCandidate.scores.interviewCompletedAt).toLocaleDateString() :
                          selectedCandidate?.scores?.interviewScheduledAt ?
                          new Date(selectedCandidate.scores.interviewScheduledAt).toLocaleDateString() : 'N/A'
                        }
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Interview Notes
                    </Typography>
                    <Typography variant="body2">
                      {selectedCandidate?.scores?.interviewFeedback || 'No feedback available'}
                    </Typography>
                  </Box>
                  
                  {/* Rating breakdown if available */}
                  {(selectedCandidate?.scores?.interviewTechnicalRating && selectedCandidate?.scores?.interviewTechnicalRating !== 'Not rated') && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Technical: {selectedCandidate.scores.interviewTechnicalRating}/5
                      </Typography>
                    </Box>
                  )}
                  
                  {(selectedCandidate?.scores?.interviewCommunicationRating && selectedCandidate?.scores?.interviewCommunicationRating !== 'Not rated') && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Communication: {selectedCandidate.scores.interviewCommunicationRating}/5
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setScoreDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OptimizedCandidatePipeline;
